import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import type { PitchFeature } from "@/types/camping";

export interface PitchFeaturesModalProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  canEdit: boolean;
}

interface FeatureRowState {
  name: string;
  show_in_online_booking: boolean;
  is_active: boolean;
}

/** Stammdaten-Verwaltung der Platz-Eigenschaften (z. B. Satellitenempfang). */
export function PitchFeaturesModal({
  open,
  onClose,
  onChanged,
  canEdit,
}: PitchFeaturesModalProps) {
  const [features, setFeatures] = useState<PitchFeature[]>([]);
  const [rows, setRows] = useState<Record<string, FeatureRowState>>({});
  const [newName, setNewName] = useState("");
  const [newOnline, setNewOnline] = useState(true);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function applyFeatures(list: PitchFeature[]) {
    setFeatures(list);
    setRows(
      Object.fromEntries(
        list.map((f) => [
          f.id,
          {
            name: f.name,
            show_in_online_booking: f.show_in_online_booking,
            is_active: f.is_active,
          },
        ]),
      ),
    );
  }

  function load() {
    setLoading(true);
    api
      .get<PitchFeature[]>("/api/v1/pitch-features")
      .then(applyFeatures)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!open) return;
    setError(null);
    setNewName("");
    setNewOnline(true);
    load();
  }, [open]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!canEdit || !newName.trim()) return;
    setError(null);
    setCreating(true);
    try {
      await api.post("/api/v1/pitch-features", {
        name: newName.trim(),
        show_in_online_booking: newOnline,
      });
      setNewName("");
      setNewOnline(true);
      load();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    } finally {
      setCreating(false);
    }
  }

  async function handleSave(feature: PitchFeature) {
    const row = rows[feature.id];
    if (!canEdit || !row || !row.name.trim()) return;
    setError(null);
    setBusyId(feature.id);
    try {
      await api.patch(`/api/v1/pitch-features/${feature.id}`, {
        name: row.name.trim(),
        show_in_online_booking: row.show_in_online_booking,
        is_active: row.is_active,
      });
      load();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(feature: PitchFeature) {
    if (!canEdit) return;
    const confirmed = window.confirm(
      `Eigenschaft „${feature.name}“ löschen?\n\nSie wird von allen Stellplätzen entfernt. Bestehende Buchungswünsche bleiben erhalten, werden aber nicht mehr berücksichtigt.`,
    );
    if (!confirmed) return;
    setError(null);
    setBusyId(feature.id);
    try {
      await api.del(`/api/v1/pitch-features/${feature.id}`);
      load();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    } finally {
      setBusyId(null);
    }
  }

  function rowChanged(feature: PitchFeature): boolean {
    const row = rows[feature.id];
    if (!row) return false;
    return (
      row.name.trim() !== feature.name ||
      row.show_in_online_booking !== feature.show_in_online_booking ||
      row.is_active !== feature.is_active
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Platz-Eigenschaften"
      description="Eigenschaften wie Satellitenempfang verwalten. Online sichtbare Eigenschaften können Gäste bei der Buchung als Wunsch angeben – sie werden berücksichtigt, solange dadurch keine anderen Gäste abgewiesen werden müssten."
      size="lg"
    >
      <div className="space-y-4">
        {canEdit ? (
          <form
            onSubmit={(e) => void handleCreate(e)}
            className="flex flex-wrap items-end gap-3 rounded-lg border border-separator bg-fill-tertiary/30 p-3"
          >
            <label className="min-w-[200px] flex-1 text-sm">
              <span className="mb-1 block text-label-secondary">Neue Eigenschaft</span>
              <input
                className="eh-input"
                placeholder="z. B. Satellitenempfang"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </label>
            <label className="flex items-center gap-2 pb-2 text-sm text-label">
              <input
                type="checkbox"
                className="rounded border-separator text-accent focus:ring-accent"
                checked={newOnline}
                onChange={(e) => setNewOnline(e.target.checked)}
              />
              In Online-Buchung sichtbar
            </label>
            <Button type="submit" loading={creating} disabled={!newName.trim()}>
              Anlegen
            </Button>
          </form>
        ) : null}

        {loading ? (
          <p className="text-sm text-label-secondary">Lade Eigenschaften…</p>
        ) : features.length === 0 ? (
          <p className="text-sm text-label-secondary">
            Noch keine Eigenschaften angelegt.
          </p>
        ) : (
          <ul className="divide-y divide-separator rounded-lg border border-separator">
            {features.map((feature) => {
              const row = rows[feature.id];
              if (!row) return null;
              return (
                <li key={feature.id} className="flex flex-wrap items-center gap-3 p-3">
                  <input
                    className="eh-input min-w-[160px] flex-1"
                    value={row.name}
                    onChange={(e) =>
                      setRows((r) => ({
                        ...r,
                        [feature.id]: { ...row, name: e.target.value },
                      }))
                    }
                    disabled={!canEdit}
                    aria-label={`Name der Eigenschaft ${feature.name}`}
                  />
                  <label className="flex items-center gap-2 text-sm text-label">
                    <input
                      type="checkbox"
                      className="rounded border-separator text-accent focus:ring-accent"
                      checked={row.show_in_online_booking}
                      onChange={(e) =>
                        setRows((r) => ({
                          ...r,
                          [feature.id]: { ...row, show_in_online_booking: e.target.checked },
                        }))
                      }
                      disabled={!canEdit}
                    />
                    Online sichtbar
                  </label>
                  <label className="flex items-center gap-2 text-sm text-label">
                    <input
                      type="checkbox"
                      className="rounded border-separator text-accent focus:ring-accent"
                      checked={row.is_active}
                      onChange={(e) =>
                        setRows((r) => ({
                          ...r,
                          [feature.id]: { ...row, is_active: e.target.checked },
                        }))
                      }
                      disabled={!canEdit}
                    />
                    Aktiv
                  </label>
                  {canEdit ? (
                    <div className="ml-auto flex gap-2">
                      {rowChanged(feature) ? (
                        <Button
                          type="button"
                          size="sm"
                          loading={busyId === feature.id}
                          disabled={!row.name.trim()}
                          onClick={() => void handleSave(feature)}
                        >
                          Speichern
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="danger"
                        loading={busyId === feature.id}
                        onClick={() => void handleDelete(feature)}
                      >
                        Löschen
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}

        <div className="eh-modal-actions flex justify-end border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
